/**
 * Broker API Configuration
 * Uses environment variables from .env files
 */

interface ApiConfig {
  baseUrl: string;
  apiPath: string;
  timeout: number;
}

const getEnvironmentConfig = (): ApiConfig => {
  // Get base URL from environment variable
  // .env.development: VUE_APP_BACKEND_URL=https://brokers-qa.swiset.com/api
  // .env.production: VUE_APP_BACKEND_URL=https://api.swiset.com/api
  const backendUrl = process.env.VUE_APP_BACKEND_URL || 'https://brokers-qa.swiset.com/api'

  // Parse the URL to extract base and path
  let baseUrl: string
  let apiPath: string

  if (backendUrl.endsWith('/api')) {
    // Remove /api from the end to get base URL
    baseUrl = backendUrl.replace(/\/api$/, '')
    apiPath = '/api/brokers'
  } else {
    // Fallback if format is different
    baseUrl = backendUrl
    apiPath = '/brokers'
  }

  return {
    baseUrl,
    apiPath,
    timeout: 30000
  }
}

const config = getEnvironmentConfig()

// Log configuration in development mode only
if (process.env.NODE_ENV === 'development') {
  console.log('Broker API Config:', {
    environment: process.env.NODE_ENV,
    VUE_APP_BACKEND_URL: process.env.VUE_APP_BACKEND_URL,
    baseUrl: config.baseUrl,
    apiPath: config.apiPath,
    fullApiUrl: `${config.baseUrl}${config.apiPath}`
  })
}

export const API_CONFIG = {
  BASE_URL: config.baseUrl,
  API_PATH: config.apiPath,
  TIMEOUT: config.timeout,

  // API Endpoints
  AUTH: {
    LOGIN: `${config.baseUrl}${config.apiPath}/admin/authenticate`,
    REFRESH: `${config.baseUrl}${config.apiPath}/admin/refresh`,
    LOGOUT: `${config.baseUrl}${config.apiPath}/admin/logout`
  },

  COMPETITIONS: {
    LIST: `${config.baseUrl}${config.apiPath}/admin/competitions`,
    CREATE: `${config.baseUrl}${config.apiPath}/admin/competitions`,
    DETAIL: (id: string) => `${config.baseUrl}${config.apiPath}/admin/competitions/${id}`,
    UPDATE: (id: string) => `${config.baseUrl}${config.apiPath}/admin/competitions/${id}`,
    DELETE: (id: string) => `${config.baseUrl}${config.apiPath}/admin/competitions/${id}`,
    // New unified endpoint for all partner tournaments
    ALL_TOURNAMENTS: `${config.baseUrl}${config.apiPath}/tournament/admin`,
    // Legacy endpoints (deprecated - use ALL_TOURNAMENTS instead)
    CLUB_COMPETITIONS: (clubId: number) => `${config.baseUrl}/api/ext/tournaments/club?clubId=${clubId}`,
    OVERVIEW: (tournamentId: number) => `${config.baseUrl}/api/ext/tournaments/${tournamentId}/overview/admin`
  },

  CLUBS: {
    LIST: `${config.baseUrl}${config.apiPath}/admin/clubs`,
    PARTNER_CLUBS: (partnerId: number) => `${config.baseUrl}${config.apiPath}/club/admin/${partnerId}`,
    OWNED_CLUBS: (partnerId: number) => `${config.baseUrl}${config.apiPath}/club/admin/${partnerId}/owned`,
    CREATE: `${config.baseUrl}${config.apiPath}/club/admin/createClub`,
    INVITE_MEMBERS: `${config.baseUrl}${config.apiPath}/club/admin/clubs/members/invite-members-emails`,
    GET_SOCIAL_INVITE_CODE: (clubId: number) => `${config.baseUrl}${config.apiPath}/club/admin/clubs/${clubId}/invite-members-by-social`,
    ADMIN_INVITATIONS: `${config.baseUrl}${config.apiPath}/club/admin/invitations`
  },

  DASHBOARD: {
    GEOGRAPHIC_INTELLIGENCE: `${config.baseUrl}${config.apiPath}/admin/clubs/geographic-intelligence-data-service`,
    OVERVIEW: `${config.baseUrl}${config.apiPath}/admin/dashboard/overview`,
    METRICS: `${config.baseUrl}${config.apiPath}/admin/dashboard/metrics`
  },

  LEAGUES: {
    OVERVIEW: (partnerId: number, assessmentType: string, leaderboardType: string) =>
      `${config.baseUrl}/api/brokers/admin/leaderboard/overview/${partnerId}/${assessmentType}/${leaderboardType}`
  },
  
  MEMBERS: {
    BASE: `${config.baseUrl}${config.apiPath}/staff`,
    MEMBERS: `${config.baseUrl}${config.apiPath}/staff/members`,
    INVITED: `${config.baseUrl}${config.apiPath}/staff/invited`,
    INVITED_RESEND: `${config.baseUrl}${config.apiPath}/staff/invited/resend`,
    DELETE_INVITE: `${config.baseUrl}${config.apiPath}/staff/invited`,
    SIGNUP_FROM_INVITE: `${config.baseUrl}${config.apiPath}/staff/signup-from-invite`
  },
  SERVERS: {
    LIST: `${config.baseUrl}${config.apiPath}/admin/servers/`,
    GROUPS: (serverId: number) => `${config.baseUrl}${config.apiPath}/admin/servers/${serverId}/groups`,
    UPDATE_NICKNAME: (groupId: number) => `${config.baseUrl}${config.apiPath}/admin/server-groups/${groupId}/nickname`
  },

  PARTNER: {
    BROKERS: (partnerId: number) => `${config.baseUrl}/api/ext/partners/brokers/${partnerId}`
  },

  FEATURE_VOTES: {
    CREATE: `${config.baseUrl}${config.apiPath}/admin/feature-votes`,
    GET_BY_USER_AND_FEATURE: `${config.baseUrl}${config.apiPath}/admin/feature-votes`,
  },
}

export default API_CONFIG